import React, { useState, useEffect, useCallback } from "react";
import Nav from "./components/Nav.jsx";
import Footer from "./components/Footer.jsx";
import IntroSplash, { shouldPlayIntro, INTRO_MAX_MS } from "./components/IntroSplash.jsx";
import useReveal from "./hooks/useReveal.js";
import { PAGES, DEFAULT_PAGE } from "./pages/registry.js";
import "./styles/global.css";

/* ============================================================================
   A4 EXPRESS — app shell.

   Routing is deliberately minimal: a single `page` key resolved against the
   registry in pages/registry.js. Swapping this for React Router or Next.js
   later means replacing the `go` callback and the <Page /> lookup below —
   the pages themselves stay untouched.

   The intro splash overlays this shell rather than replacing it. The site
   below renders normally from the very first frame; only its opacity is held
   back. That is what makes the handover seamless — there is nothing left to
   mount, load or lay out when the logo slides away.
   ========================================================================== */

export default function App() {
  const [page, setPage] = useState(DEFAULT_PAGE);
  // Decided once, synchronously, before the first paint — so the site is never
  // briefly visible before the overlay covers it.
  const [intro] = useState(shouldPlayIntro);
  const [revealed, setRevealed] = useState(!intro);
  useReveal(page);

  const reveal = useCallback(() => setRevealed(true), []);

  // Failsafe: if anything at all goes wrong in the intro — a thrown error, an
  // asset that never resolves, a browser that skips the transition — the site
  // becomes visible anyway. A splash screen must never be able to trap a
  // visitor on a blank page.
  useEffect(() => {
    if (!intro || revealed) return;
    const t = setTimeout(() => setRevealed(true), INTRO_MAX_MS);
    return () => clearTimeout(t);
  }, [intro, revealed]);

  const go = useCallback((key) => {
    setPage(PAGES[key] ? key : DEFAULT_PAGE);
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  }, []);

  const current = PAGES[page] || PAGES[DEFAULT_PAGE];

  useEffect(() => {
    document.title = current.title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "description";
      document.head.appendChild(m);
    }
    m.content = current.description;
  }, [current]);

  const Page = current.component;

  return (
    <>
      {intro && <IntroSplash onReveal={reveal} />}
      <div className={"a4" + (revealed ? " site-in" : " pre-intro")}>
        <a className="skip" href="#main">Skip to content</a>
        <Nav page={page} go={go} />
        <main id="main">
          <Page go={go} />
        </main>
        <Footer go={go} />
      </div>
    </>
  );
}
